import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useMeta } from "@/hooks/useMeta";
import { useFilters } from "@/hooks/useFilters";
import PageHeader from "@/components/common/PageHeader";
import FilterBar from "@/components/common/FilterBar";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import LogActivityDialog from "@/components/activities/LogActivityDialog";
import FollowupsPanel from "@/components/activities/FollowupsPanel";
import ActivityList from "@/components/activities/ActivityList";
import ActivityTimeline from "@/components/activities/ActivityTimeline";
import ActivityCalendar from "@/components/activities/ActivityCalendar";
import { Plus, ListChecks, List, CalendarDays, GitCommitVertical } from "lucide-react";

const VIEWS = [
  { value: "followups", label: "Follow-ups", icon: ListChecks },
  { value: "list", label: "List", icon: List },
  { value: "calendar", label: "Calendar", icon: CalendarDays },
  { value: "timeline", label: "Timeline", icon: GitCommitVertical },
];

export default function Activities() {
  const { user } = useAuth();
  const meta = useMeta();
  const { filters, setFilters, apiParams, params } = useFilters();
  const view = params.get("view") || "followups";
  const type = params.get("type") || "";
  const [open, setOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const canLog = user?.role !== "sales_partner";
  const dateApplies = view === "list" || view === "timeline";

  return (
    <div className="space-y-6">
      <PageHeader title="Activities & Follow-ups" subtitle="Calls, visits, demos and reminders — internal CRM only, no external calendar." testid="activities-header"
        actions={canLog && <Button onClick={() => setOpen(true)} className="bg-teal hover:bg-teal-700 h-11" data-testid="log-activity-btn"><Plus className="h-4 w-4" /> Log Activity</Button>} />

      <FilterBar filters={filters} setFilters={setFilters} testid="activities-filter-bar">
        {dateApplies && (
          <Select value={type || "all"} onValueChange={(v) => setFilters({ type: v === "all" ? "" : v })}>
            <SelectTrigger className="h-11 w-full sm:w-44" data-testid="activities-type-filter"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {(meta.activity_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </FilterBar>
      {!dateApplies && filters.period !== "all" && (
        <p className="text-xs text-slate-500 -mt-3" data-testid="activities-date-note">
          {view === "calendar" ? "Calendar uses its own month navigation; the salesperson filter still applies." : "Follow-ups are grouped by due date; the salesperson filter still applies."}
        </p>
      )}

      <Tabs value={view} onValueChange={(v) => setFilters({ view: v })}>
        <div className="overflow-x-auto -mx-1 px-1 pb-1">
          <TabsList data-testid="activities-tabs" className="w-max">
            {VIEWS.map((v) => (
              <TabsTrigger key={v.value} value={v.value} data-testid={`tab-${v.value}`} className="gap-1.5"><v.icon className="h-4 w-4" /> {v.label}</TabsTrigger>
            ))}
          </TabsList>
        </div>
        <TabsContent value="followups" className="mt-4"><FollowupsPanel salesperson={apiParams.salesperson} currentUserId={user?.id} refreshKey={refreshKey} /></TabsContent>
        <TabsContent value="list" className="mt-4"><ActivityList apiParams={apiParams} type={type} refreshKey={refreshKey} /></TabsContent>
        <TabsContent value="calendar" className="mt-4"><ActivityCalendar salesperson={apiParams.salesperson} refreshKey={refreshKey} /></TabsContent>
        <TabsContent value="timeline" className="mt-4"><ActivityTimeline apiParams={apiParams} type={type} refreshKey={refreshKey} /></TabsContent>
      </Tabs>

      <LogActivityDialog open={open} onOpenChange={setOpen} onSaved={() => setRefreshKey((k) => k + 1)} />
    </div>
  );
}
